import { createFileRoute, Outlet, useParams } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { AccountCard, useSipAccounts } from '@/entities/telephony'
import { errorMessage } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { EmptyState } from '@/shared/ui/empty-state'
import { Icon } from '@/shared/ui/icon'
import { PageHeader } from '@/shared/ui/page-header'
import { PillTabs } from '@/shared/ui/pill-tabs'
import { Spinner } from '@/shared/ui/spinner'
import { AccountForm } from '@/widgets/account-form'

export const Route = createFileRoute('/telephony/accounts')({
  component: AccountsPage,
  staticData: { crumb: 'Учётки' },
})

type Filter = 'all' | 'active' | 'problem' | 'off'

const PROBLEM = new Set(['unverified', 'verifying', 'degraded', 'failed'])

// Экспорт нужен компонентному тесту: сам роут собирает его из этого же модуля.
export function AccountsPage() {
  const { accounts, isPending, isError, error, refetch } = useSipAccounts()
  const { accountId } = useParams({ strict: false })
  const [filter, setFilter] = useState<Filter>('all')
  const [registerOpen, setRegisterOpen] = useState(false)

  const filtered = useMemo(
    () =>
      accounts.filter((a) => {
        if (filter === 'active') return a.status === 'active'
        if (filter === 'problem') return PROBLEM.has(a.status)
        if (filter === 'off') return a.status === 'disabled'
        return true
      }),
    [accounts, filter],
  )

  const counts = useMemo(
    () => ({
      all: accounts.length,
      active: accounts.filter((a) => a.status === 'active').length,
      problem: accounts.filter((a) => PROBLEM.has(a.status)).length,
      off: accounts.filter((a) => a.status === 'disabled').length,
    }),
    [accounts],
  )

  return (
    <>
      <PageHeader
        title="SIP-учётки"
        description="Учётки операторов: верификация, каналы, DID-номера"
        actions={
          <Button variant="accent" onClick={() => setRegisterOpen(true)}>
            <Icon name="plus" size={18} />
            Завести учётку
          </Button>
        }
      />

      {isPending ? (
        <div className="flex justify-center py-24 text-muted">
          <Spinner size={28} />
        </div>
      ) : isError ? (
        <EmptyState
          icon="alert"
          title="Не удалось загрузить учётки"
          description={errorMessage(error)}
          action={
            <Button size="sm" onClick={refetch}>
              Повторить
            </Button>
          }
        />
      ) : accounts.length === 0 ? (
        <EmptyState
          icon="id-card"
          title="Учёток пока нет"
          description="Заведи платформенную или BYOC-учётку у оператора"
          action={
            <Button variant="accent" size="sm" onClick={() => setRegisterOpen(true)}>
              Завести учётку
            </Button>
          }
        />
      ) : (
        <div className="flex items-start gap-6">
          <div className="flex w-[400px] shrink-0 flex-col gap-3">
            <PillTabs
              aria-label="Фильтр учёток"
              value={filter}
              onChange={setFilter}
              options={[
                { value: 'all', label: 'Все', count: counts.all },
                { value: 'active', label: 'Активные', count: counts.active },
                { value: 'problem', label: 'Проблемы', count: counts.problem },
                { value: 'off', label: 'Выкл', count: counts.off },
              ]}
            />
            <div className="flex flex-col gap-2">
              {filtered.length === 0 && (
                <EmptyState
                  icon="id-card"
                  title="Пусто"
                  description="Под фильтр ничего не попало"
                />
              )}
              {filtered.map((account) => (
                <AccountCard key={account.id} account={account} active={accountId === account.id} />
              ))}
            </div>
          </div>

          <div className="min-w-0 flex-1">
            <Outlet />
          </div>
        </div>
      )}

      <AccountForm
        key={registerOpen ? 'open' : 'closed'}
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
      />
    </>
  )
}
